import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState, useRef } from "react";
import CrossIcon from "../../resources/icons/x-close.svg";
import CfLoader from "../CfLoader/CfLoader";
import RedirectPage from "../../resources/icons/redirect.svg";
import DisabledRedirectPage from "../../resources/icons/disabled-redirect.svg";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { formatDate } from "../../utils/formatDate";

const CredentialPreviewModal = (props) => {
  const ImageLoading = (
    <svg
      width="full"
      className="h-[17rem] sm:h-[18.875rem]"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="100%" height="100%" fill="#f0f0f0" />
      <foreignObject
        x="91%"
        y="91%"
        width="200"
        height="200"
        style={{ transform: "translate(-50%, -50%)" }}
      >
        <div xmlns="http://www.w3.org/1999/xhtml">
          <CfLoader />
        </div>
      </foreignObject>
    </svg>
  );
  const {
    credential_id,
    issuer_name,
    email_id,
    mobile_number,
    recipient_name,
    status,
    updated_at,
    svg_url,
    openCloseModal,
    isOpen,
    org_name,
    public_verify_url,
    pdf_url,
  } = props;
  const [currentImg, setCurrentImg] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const pdfCanvasRef = useRef(null);

  useEffect(() => {
    if (pdfjsLib.GlobalWorkerOptions.workerSrc !== pdfjsWorker) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
    }
  }, []);

  const fetchImagePreview = () => {
    fetch(svg_url)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Network response was not ok");
        }
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setCurrentImg(url);
        setImageLoadError(false);
      })
      .catch((error) => {
        console.error(error);
        setImageLoadError(true);
      });
  };

  const renderPdfPreview = async () => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdf_url);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.0 });

      const canvas = pdfCanvasRef.current;
      const context = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
      setImageLoadError(false);
    } catch (error) {
      console.error("Error rendering PDF:", error);
      setImageLoadError(true);
    }
  };

  useEffect(() => {
    // Check if the URL is a PDF
    if (pdf_url && (pdf_url.endsWith(".pdf") || pdf_url.includes(".pdf?"))) {
      setIsPdf(true);
      renderPdfPreview();
    } else if (svg_url) {
      setIsPdf(false);
      fetchImagePreview();
    } else {
      setCurrentImg();
      setImageLoadError(true);
    }
  }, [svg_url, pdf_url]);

  const handleKeyDown = (event, callback) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      callback();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => openCloseModal(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 top-20 bg-black/30  backdrop-blur-sm " />
        </Transition.Child>

        <div className="fixed inset-0 top-20 overflow-y-auto px-4 md:px-0 max-h-full">
          <div className="flex items-center min-h-full justify-center text-center ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg max-h-[85vh] transform overflow-x-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all overflow-y-auto">
                <div className="flex items-center justify-between py-[1.313rem] px-8 font-medium">
                  <p className="flex flex-col sm:flex-row">
                    Certificate preview {credential_id && "-"}{" "}
                    <div className="flex">
                      {credential_id ? (
                        <div className="ml-[0.3rem]">{credential_id}</div>
                      ) : (
                        "Unavailable"
                      )}
                      {public_verify_url ? (
                        <button
                          className="ml-[0.8rem]"
                          onClick={() => {
                            const certHash =
                              public_verify_url.split("/verify/")[1];
                            window.open(`/credentials/${certHash}`, "_blank");
                          }}
                          onKeyDown={(e) =>
                            handleKeyDown(e, () => {
                              const certHash =
                                public_verify_url.split("/verify/")[1];
                              window.open(`/credentials/${certHash}`, "_blank");
                            })
                          }
                          title="Verification Page"
                          aria-label="Open verification page"
                        >
                          <img
                            src={RedirectPage}
                            alt="RedirectPage"
                            className="w-[1.3rem]"
                          />
                        </button>
                      ) : (
                        <div className="ml-[0.8rem]">
                          <img
                            src={DisabledRedirectPage}
                            alt="Disabled redirect"
                            className="w-[1.3rem] cursor-not-allowed"
                          />
                        </div>
                      )}
                    </div>
                  </p>
                  <div>
                    <button
                      onClick={() => openCloseModal(false)}
                      onKeyDown={(e) =>
                        handleKeyDown(e, () => openCloseModal(false))
                      }
                      className="focus:outline-none"
                      aria-label="Close modal"
                    >
                      <img src={CrossIcon} alt="Close" className="w-[1.3rem]" />
                    </button>
                  </div>
                </div>
                <div>
                  <div className="w-full bg-[#F2F4F7]">
                    {isPdf ? (
                      <div className="flex items-center justify-center p-4">
                        <canvas
                          ref={pdfCanvasRef}
                          className="max-w-full max-h-[400px]"
                        />
                      </div>
                    ) : currentImg && !imageLoadError ? (
                      <img
                        src={currentImg}
                        alt="Certificate preview"
                        className="w-full px-12 py-[0.875rem]"
                      />
                    ) : (
                      ImageLoading
                    )}
                  </div>
                  <div className="p-6">
                    <div>
                      <div className="text-cfTableHeaderText font-bold text-[0.875rem] pb-2">
                        <p>Certificate Details</p>
                      </div>
                      <div className="text-[#242F46] pb-3">
                        <span className="flex items-center text-[0.875rem] pb-[1rem]">
                          <p>Name:</p>&nbsp;
                          <p>
                            {recipient_name ? recipient_name : "Unavailable"}
                          </p>
                        </span>
                        <span className="flex items-center text-[0.875rem] pb-[1rem]">
                          <p>Email id:</p>&nbsp;
                          <p>{email_id ? email_id : "Unavailable"}</p>
                        </span>
                        <span className="flex items-center text-[0.875rem] pb-[1rem]">
                          <p>Phone number:</p>&nbsp;
                          <p>{mobile_number ? mobile_number : "Unavailable"}</p>
                        </span>
                        {/* <span className="flex items-center text-[0.875rem] pb-[1rem]">
                          <p>WhatsApp number:</p>&nbsp;
                          <p>
                            {whatsapp_number ? whatsapp_number : "Unavailable"}
                          </p>
                        </span> */}
                      </div>
                    </div>
                    <hr />
                    <div>
                      <div className="flex items-center justify-between pb-2 pt-3">
                        <div className="text-cfTableHeaderText font-bold text-[0.875rem]">
                          <p>Issuance Details</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="text-cfTableHeaderText font-bold text-[0.92rem]">
                            Status
                          </p>
                          <p
                            className={`px-[1.125rem] py-[0.125rem] text-cfSecondary rounded-full ${
                              status === "Issued"
                                ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                                : status === "Failed"
                                ? "bg-cfIssuedCredentialTemplateCardStatusUnderReviewBG"
                                : status === "Revoked"
                                ? "bg-cfIssuedCredentialTemplateCardStatusRevokedBG"
                                : !status && " bg-gray-300 "
                            }`}
                          >
                            {status ? status : "Unavailable"}
                          </p>
                        </div>
                      </div>
                      <div className="text-[#242F46] mt-[0.4rem] ">
                        <span className="flex items-center text-[0.875rem] pb-[1rem]">
                          <p>Issued by:</p>&nbsp;
                          <p>{issuer_name ? issuer_name : "Unavailable"}</p>
                          {org_name && (
                            <p>
                              ,&nbsp;
                              {org_name ? org_name : "Unavailable"}
                            </p>
                          )}
                        </span>
                        <span className="flex items-center text-[0.875rem] pb-[1rem]">
                          <p>Issuance date:</p>&nbsp;
                          <p>
                            {updated_at
                              ? formatDate(updated_at)
                              : "Unavailable"}
                          </p>
                        </span>
                      </div>
                    </div>
                    {/* <hr />
                      <div>
                       <div>
                        <span>
                          <img src={TimestampClockIcon} alt="" />
                        </span>
                        <div>
                          <p>Certificate re-issued via email and whatsapp</p>
                          <p>19 March 2024  | 10:40 AM</p>
                        </div>
                       </div>
                       <hr />
                      </div> */}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CredentialPreviewModal;
